import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { ErrorNotice } from "../components/ui/Feedback";
import { Field } from "../components/ui/Input";
import { MicroLabel } from "../components/ui/Text";
import { ApiError, post } from "../lib/api";
import { MIN_PASSWORD_LENGTH, passwordIssues, readLinkToken } from "../lib/operator";
import type { SignInLinkDetails } from "../lib/types";

/**
 * Where a sign-in link lands: an invitation to choose a first password, or a reset.
 *
 * Outside `RequireAuth`, because the person opening it has no session — and for an
 * invitation, has never had one.
 *
 * ## The token never leaves the fragment
 *
 * It arrives after `#`, which the browser does not send in any request, so it is in
 * no access log. It is read once, then removed from the address bar with
 * `history.replaceState` — so it is not left in the browser's history, in a
 * screenshot of the tab, or in whatever the person bookmarks next. From then on it
 * exists only in this component's state and in the two POST bodies that need it.
 *
 * ## One message for every bad link
 *
 * Unknown, used, revoked and expired all read the same. The server does not
 * distinguish them either, and a page that did would tell whoever holds a guessed
 * token which of those it had found.
 *
 * Choosing the password does not sign the person in. They are sent to the login
 * page to use it once, which is the moment a typo in a password manager is found —
 * here, while the link's owner is still paying attention, rather than tomorrow.
 */
export function SetPassword() {
  const navigate = useNavigate();
  const [token] = useState(() => readLinkToken(window.location.hash));
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    if (window.location.hash === "") return;
    window.history.replaceState(window.history.state, "", window.location.pathname);
  }, []);

  const details = useQuery({
    queryKey: ["sign-in-link", token],
    queryFn: () => post<SignInLinkDetails>("/auth/sign-in-link/inspect", { token }),
    enabled: token !== undefined,
    // A 404 is the answer, not a fault, and a retry would spend the rate limit.
    retry: false,
    // The token is a credential; nothing should keep it, or its answer, around.
    gcTime: 0,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const redeem = useMutation({
    mutationFn: () => post<{ email: string }>("/auth/sign-in-link/redeem", { token, password }),
    onSuccess: ({ email }) => {
      navigate("/login", {
        replace: true,
        state: { email, notice: "Password set. Sign in with it now." },
      });
    },
  });

  const issues = passwordIssues(password, confirm);
  const canSubmit =
    password.length >= MIN_PASSWORD_LENGTH &&
    confirm === password &&
    issues.password === undefined &&
    !redeem.isPending;

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (canSubmit) redeem.mutate();
  }

  const isInvalid =
    token === undefined ||
    (details.isError && details.error instanceof ApiError && details.error.status === 404);

  return (
    <div className="flex min-h-dvh flex-col justify-center bg-paper px-gutter py-section lg:px-section">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="font-display text-title font-semibold text-ink">
            OCPP<span className="text-signal">.</span>
          </span>
          <MicroLabel>Operator access</MicroLabel>
        </div>

        {isInvalid ? (
          <InvalidLink />
        ) : details.isError ? (
          <ErrorNotice error={details.error} onRetry={() => void details.refetch()} />
        ) : details.data === undefined ? (
          <MicroLabel>Checking link</MicroLabel>
        ) : (
          <form onSubmit={onSubmit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h1 className="m-0 font-display text-title font-semibold text-ink">
                {details.data.purpose === "invite"
                  ? `Welcome, ${details.data.name}`
                  : `Reset your password, ${details.data.name}`}
              </h1>
              <p className="m-0 text-body text-ink-muted">
                {details.data.purpose === "invite"
                  ? "Choose a password for your new operator account."
                  : "Choose a new password. Every device signed in to this account will be signed out."}
              </p>
              <div className="mt-1 h-0.5 w-12 bg-rule-strong" />
            </div>

            {/* For the password manager: it saves the pair under this address. */}
            <input
              type="email"
              name="email"
              value={details.data.email}
              autoComplete="username"
              readOnly
              hidden
            />
            <MicroLabel>{details.data.email}</MicroLabel>

            {redeem.isError ? <ErrorNotice error={redeem.error} /> : null}

            <Field
              label="New password"
              type="password"
              name="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              required
              autoFocus
              hint={`At least ${MIN_PASSWORD_LENGTH} characters. A phrase is easier to type than a jumble.`}
              {...(issues.password === undefined ? {} : { error: issues.password })}
            />

            <Field
              label="Confirm password"
              type="password"
              name="confirm-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              autoComplete="new-password"
              required
              {...(issues.confirm === undefined ? {} : { error: issues.confirm })}
            />

            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit}
              pending={redeem.isPending}
              className="self-start"
            >
              Set password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

function InvalidLink() {
  return (
    <div className="flex flex-col gap-3">
      <h1 className="m-0 font-display text-title font-semibold text-ink">
        This link is invalid or has expired
      </h1>
      <p className="m-0 max-w-prose text-body text-ink-muted">
        Sign-in links work once and only for a limited time. Ask an admin for a new one.
      </p>
      <Link to="/login" className="text-body text-ink underline decoration-1 underline-offset-4">
        Go to sign in
      </Link>
    </div>
  );
}
